import dayjs from 'dayjs'
// useBreaksData.ts
import React, { useEffect } from 'react'

import { upsertOneWidgetWindow } from '@/entities/widgetWindows'
import { CustomGridToolbar } from '@/shared/components/GridTable/CustomGridToolbar'
import { ColourGridCell } from '@/shared/components/GridTable/GridCells/ColourGridCell'
import { GridTable } from '@/shared/components/GridTable/GridTable'
import { TOOL_WIDGETS, WidgetTypeWithSuffix } from '@/shared/components/Mosaic/models/widgetType'
import { useAppDispatch } from '@/shared/store'
import { faMugSaucer } from '@fortawesome/pro-regular-svg-icons'
import { useTheme } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid-premium'

import { useBreaksData } from './libs/useBreaksTableData'

export type BreaksTableWidgetProps = {
  widgetId: WidgetTypeWithSuffix
}

export const BreaksTableWidget: React.FC<BreaksTableWidgetProps> = ({ widgetId }) => {
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const { breaks, loading } = useBreaksData()

  // Update widget window context
  useEffect(() => {
    if (widgetId) {
      dispatch(upsertOneWidgetWindow({
        id: widgetId,
        isActive: true,
        widgetType: TOOL_WIDGETS.RunsheetBreaks,
        navigations: [
          {
            title: 'Breaks',
            icon: faMugSaucer,
          },
        ],
      }))
    }
  }, [widgetId, dispatch])


  const getStatusCellStyle = (status: string) => {
    if (status === 'On Break') {
      return {
        statusCellStyle: {
          backgroundColor: theme.palette.info.main,
          color: theme.palette.getContrastText(theme.palette.info.main),
        },
      }
    } else if (status === 'Not Taken') {
      return {
        statusCellStyle: {
          backgroundColor: theme.palette.error.main,
          color: theme.palette.getContrastText(theme.palette.error.main),
        },
      }
    }
    else if (status === 'Completed') {
      return {
        statusCellStyle: {
          backgroundColor: theme.palette.success.main,
          color: theme.palette.getContrastText(theme.palette.success.main),
        },
      }
    }
    else if (status === 'Unplanned') {
      return {
        statusCellStyle: {
          backgroundColor: theme.palette.warning.main,
          color: theme.palette.getContrastText(theme.palette.warning.main),
        },
      }
    }

    return {}
  }

  // Construct rows and columns
  const rows = breaks.map((breakItem, idx) => ({
    id: idx,
    ...breakItem,
    ...getStatusCellStyle(breakItem.StatusDescription),
    statusType: breakItem.StatusDescription,
  }))

  const columns: GridColDef[] = [
    {
      field: 'RunsheetName',
      headerName: 'Runsheet',
      width: 100,
      pinnable: true,
      filterable: true,
      sortable: true,
      description: 'Runsheet having the break',
    },
    {
      field: 'Run',
      headerName: 'Run',
      width: 100,
      pinnable: true,
      filterable: true,
      sortable: true,
      description: 'Run having the break',
    },
    {
      field: 'PlannedArrival',
      headerName: 'PTA [ETA]',
      width: 100,
      valueGetter: (val) => {
        return dayjs(val).format('HH:mm')
      },
      filterable: true,
      sortable: true,
      description: 'Planned Time Arrival [Estimated Time Arrival]',
    },
    {
      field: 'StatusDescription',
      headerName: 'Status',
      width: 100,
      filterable: true,
      sortable: true,
      description: 'Status',
      cellClassName: (params) => params.value,
      renderCell: (params) => (<ColourGridCell {...params} />),
      valueGetter: (value, row) => {
        return {
          text: value,
          cellStyle: row.statusCellStyle,
        }
      },
    },
    {
      field: 'violation',
      headerName: 'Violation',
      width: 140,
      valueGetter: (_, row) => {
        if (row.IsLate) {
          return `Late by ${row.ViolationMinLate} mins`
        }

        return ''
      },
      filterable: true,
      description: 'Violation description',
    },
    {
      field: 'actuals',
      headerName: 'Actuals',
      width: 130,
      valueGetter: (_, row) => {
        const start = row.BreakStart ? dayjs(row.BreakStart).format('HH:mm') : ''
        const finish = row.BreakFinish ? dayjs(row.BreakFinish).format('HH:mm') : ''

        return start && finish ? `${start} - ${finish}` : `${start} - `
      },
      filterable: true,
      sortable: true,
      description: 'Actual start and finish time of break',
    },
    {
      field: 'actualBreakDuration',
      headerName: 'Actual Break Duration',
      width: 130,
      valueGetter: (_, row) => {
        if (!row.BreakStart || !row.BreakFinish) return ''

        const start = new Date(row.BreakStart)
        const finish = new Date(row.BreakFinish)
        const diffMins = Math.round((finish.getTime() - start.getTime()) / 60000)

        return `${diffMins} minutes`
      },
      filterable: true,
      sortable: true,
      description: 'Actual break duration recorded',
    },
  ]

  return (
    <GridTable
      rows={rows}
      columns={columns}
      loading={loading}
      isDragDropEnabled={false}
      slots={{
        toolbar: CustomGridToolbar,
      }}
      sx={{
        '.MuiDataGrid-filler--pinnedRight': { display: 'none' },
      }}
    />
  )
}